import React, { useEffect, useState } from "react";
import { Coins, Search, Film, Image as ImageIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Row = ({ u, onGranted }) => {
  const [v, setV] = useState("");
  const [im, setIm] = useState("");
  const [busy, setBusy] = useState(false);
  const grant = async () => {
    const videos = parseInt(v || "0", 10), images = parseInt(im || "0", 10);
    if (!videos && !images) return toast.error("أدخل عدد فيديوهات أو صور");
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${u.id}/grant`, { videos, images, note: "منحة من الإدارة" });
      onGranted(r.data); setV(""); setIm("");
      toast.success(`تمت الإضافة لـ ${u.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر المنح"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-center rounded-xl border border-border bg-secondary/30 p-3" data-testid={`credit-row-${u.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold truncate" data-testid="credit-user-email">{u.email}</span>
          {u.name && <span className="text-xs text-muted-foreground">{u.name}</span>}
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/15 text-primary">{u.plan}</span>
          {u.role === "admin" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent/20 text-accent">مشرف</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-1 flex gap-4 flex-wrap">
          <span className="flex items-center gap-1"><Film className="w-3 h-3" /> أنتج {u.videos} فيديو · منحة {u.grant_videos} (متبقي <b className="text-foreground" data-testid="credit-left-videos">{u.grant_left_videos}</b>)</span>
          <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> أنتج {u.images} صورة · منحة {u.grant_images} (متبقي <b className="text-foreground" data-testid="credit-left-images">{u.grant_left_images}</b>)</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Input type="number" value={v} onChange={(e) => setV(e.target.value)} placeholder="+ فيديو" data-optional="true" className="w-24 bg-secondary border-border h-9" data-testid="credit-videos-input" />
        <Input type="number" value={im} onChange={(e) => setIm(e.target.value)} placeholder="+ صور" data-optional="true" className="w-24 bg-secondary border-border h-9" data-testid="credit-images-input" />
        <Button size="sm" onClick={grant} disabled={busy} className="gap-1 h-9" data-testid="credit-grant-btn"><Plus className="w-4 h-4" /> منح</Button>
      </div>
    </div>
  );
};

export const AdminCredits = () => {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (query) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: { q: query || "" } });
      setUsers(r.data.users || []); setGrants(r.data.recent_grants || []);
    } catch { toast.error("تعذّر تحميل المستخدمين"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 350); return () => clearTimeout(t); }, [q]);

  const onGranted = (row) => setUsers((xs) => xs.map((x) => (x.id === row.id ? row : x)));

  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="admin-credits">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-lg flex items-center gap-2"><Coins className="w-5 h-5 text-primary" /> أرصدة المستخدمين</h2>
          <p className="text-xs text-muted-foreground mt-1">امنح أي حساب رصيد فيديوهات أو صور إضافية فوق باقته أو تجربته (للاختبارات، العملاء المميزين، التعويضات). الأرقام السالبة تسحب الرصيد.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالبريد أو الاسم أو الجوال" data-optional="true" className="pe-9 bg-secondary border-border" data-testid="credit-search" />
        </div>
      </div>
      <div className="space-y-2">
        {loading && !users.length && <p className="text-sm text-muted-foreground">جارٍ التحميل…</p>}
        {!loading && !users.length && <p className="text-sm text-muted-foreground" data-testid="credit-empty">لا يوجد مستخدم مطابق</p>}
        {users.map((u) => <Row key={u.id} u={u} onGranted={onGranted} />)}
      </div>
      {grants.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer font-bold">آخر المنح ({grants.length})</summary>
          <ul className="mt-2 space-y-1">
            {grants.map((g) => <li key={g.id} data-testid="credit-grant-log">{g.created_at?.slice(0, 16).replace("T", " ")} — {g.email}: {g.videos ? `${g.videos} فيديو ` : ""}{g.images ? `${g.images} صورة` : ""} <span className="opacity-60">({g.by})</span></li>)}
          </ul>
        </details>
      )}
    </div>
  );
};
